import { FC, useEffect, useState } from 'react'
import SVG from 'react-inlinesvg'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import {
  cancelPayout,
  getPayoutDetail,
  togglePayoutHold,
} from '../../../services/payoutsService'
import { showErrorAlert } from '../../../helpers/alert'
import { hasRole } from '../../../utils/permissions'
import { UserRole } from '../../../constants/roles'
import { RootState } from '../../../../setup'
import { shallowEqual, useSelector } from 'react-redux'
import { UserModel } from '../../../modules/auth/models/UserModel'
import RegisterTransferModal from './RegisterTransferModal'

interface Props {
  payoutId: number | null
  onClose: () => void
  onChanged: () => void
}

const STATUS_META: Record<string, { label: string; cls: string }> = {
  pending_payout: { label: 'Pending Payout', cls: 'pending' },
  in_transit: { label: 'In Transit', cls: 'in-transit' },
  paid: { label: 'Paid', cls: 'paid' },
  on_hold: { label: 'On Hold', cls: 'on-hold' },
  cancelled: { label: 'Cancelled', cls: 'cancelled' },
  refunded: { label: 'Refunded', cls: 'refunded' },
}

const currency = (amount: any, code: string) => {
  const value = typeof amount === 'string' ? Number(amount) : Number(amount ?? 0)
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: code || 'USD' }).format(value)
  } catch {
    return `${value.toFixed(2)} ${code}`
  }
}

const initials = (user: any) => {
  if (!user) return '·'
  const f = (user.firstName || '').trim()[0] || ''
  const l = (user.lastName || '').trim()[0] || ''
  const initial = `${f}${l}`.trim()
  return initial || (user.email || '·').slice(0, 1).toUpperCase()
}

const fullName = (user: any) => {
  if (!user) return '—'
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
  return name || user.email
}

const PayoutDetailDrawer: FC<Props> = ({ payoutId, onClose, onChanged }) => {
  const [payout, setPayout] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const user = useSelector<RootState>(({ auth }) => auth.user, shallowEqual) as UserModel
  const isAdmin = hasRole(user, UserRole.ADMIN)

  const fetchPayout = async (id: number) => {
    setLoading(true)
    try {
      const data = await getPayoutDetail(id)
      setPayout(data)
    } catch {
      showErrorAlert({ title: 'Error', message: 'Could not load the payout.' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (payoutId != null) fetchPayout(payoutId)
    else setPayout(null)
  }, [payoutId])

  if (payoutId == null) return null

  const closeAll = () => {
    setShowTransferModal(false)
    onClose()
  }

  const handleHoldToggle = async () => {
    if (!payout) return
    const isOnHold = payout.status === 'on_hold'
    let reason: string | undefined
    if (!isOnHold) {
      const input = window.prompt('Reason for putting this payout on hold:')
      if (!input || input.trim().length < 5) {
        showErrorAlert({ title: 'Validation', message: 'Reason must be at least 5 characters.' })
        return
      }
      reason = input.trim()
    }
    try {
      await togglePayoutHold(payout.id, reason)
      await fetchPayout(payout.id)
      onChanged()
    } catch {
      showErrorAlert({ title: 'Error', message: 'Could not toggle the hold state.' })
    }
  }

  const handleCancel = async () => {
    if (!payout) return
    const reason = window.prompt('Reason for cancelling this payout:')
    if (!reason || reason.trim().length < 5) {
      showErrorAlert({ title: 'Validation', message: 'Reason must be at least 5 characters.' })
      return
    }
    if (!window.confirm('This action cannot be undone. Cancel the payout?')) return
    try {
      await cancelPayout(payout.id, reason.trim())
      await fetchPayout(payout.id)
      onChanged()
    } catch {
      showErrorAlert({ title: 'Error', message: 'Could not cancel the payout.' })
    }
  }

  const status = payout && STATUS_META[payout.status]
  const canRegisterTransfer =
    payout && !['paid', 'cancelled', 'refunded', 'on_hold'].includes(payout.status)

  return (
    <>
      <div className='nb-payout-drawer__overlay' onClick={closeAll} />
      <div className='nb-payout-drawer' onClick={(e) => e.stopPropagation()}>
        <div className='nb-payout-drawer__header'>
          <div>
            <h3 className='nb-payout-drawer__title'>Payout {payout ? `#${payout.id}` : ''}</h3>
            {status && (
              <div style={{ marginTop: 6 }}>
                <span className={`nb-payouts__status nb-payouts__status--${status.cls}`}>
                  {status.label}
                </span>
              </div>
            )}
          </div>
          <div className='rm-style-3 cursor-pointer' onClick={closeAll}>
            <SVG src={toAbsoluteUrl('/media/svg/nobilis/closeX.svg')} className='rm-style-4' />
          </div>
        </div>

        {loading || !payout ? (
          <div style={{ color: '#808080' }}>Loading…</div>
        ) : (
          <>
            <div className='nb-payout-drawer__section'>
              <div className='nb-payout-drawer__section-title'>Source Action</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{payout.actionLabel}</div>
              <div style={{ color: '#808080', fontSize: 12 }}>
                {payout.actionType} · charged {new Date(payout.chargedAt).toLocaleString()}
              </div>
              <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span className='nb-payouts__avatar'>{initials(payout.member)}</span>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#808080' }}>Member</div>
                    <div style={{ fontSize: 13 }}>{fullName(payout.member)}</div>
                  </div>
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span className='nb-payouts__avatar'>{initials(payout.partner)}</span>
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#808080' }}>Partner</div>
                    <div style={{ fontSize: 13 }}>{fullName(payout.partner)}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className='nb-payout-drawer__section'>
              <div className='nb-payout-drawer__section-title'>Financial Breakdown</div>
              <table className='nb-payout-drawer__breakdown'>
                <tbody>
                  <tr>
                    <td className='muted'>Gross charged</td>
                    <td>{currency(payout.grossAmount, payout.currency)}</td>
                  </tr>
                  <tr>
                    <td className='muted'>Commission ({payout.commissionPercent}%)</td>
                    <td>{currency(payout.commissionAmount, payout.currency)}</td>
                  </tr>
                  <tr>
                    <td className='strong'>Payout to partner</td>
                    <td className='strong'>{currency(payout.payoutAmount, payout.currency)}</td>
                  </tr>
                  <tr>
                    <td className='muted'>Outstanding balance</td>
                    <td>{currency(payout.outstandingBalance, payout.currency)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className='nb-payout-drawer__section'>
              <div className='nb-payout-drawer__section-title'>Stripe reference</div>
              <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#151515' }}>
                {payout.stripePaymentIntentId}
              </div>
            </div>

            <div className='nb-payout-drawer__section'>
              <div className='nb-payout-drawer__section-title'>
                Transfer history · {payout.transfers?.length ?? 0}
              </div>
              {payout.transfers?.length ? (
                payout.transfers.map((t: any) => (
                  <div key={t.id} className='nb-payout-drawer__transfer'>
                    <div>
                      <strong>{currency(t.amount, t.currency)}</strong>
                      {' · '}
                      <span className={`nb-payouts__status nb-payouts__status--${STATUS_META[t.markedAs]?.cls ?? 'in-transit'}`}>
                        {STATUS_META[t.markedAs]?.label ?? t.markedAs}
                      </span>
                      {' · '}
                      <span style={{ color: '#808080' }}>{t.transferDate}</span>
                    </div>
                    <div style={{ color: '#808080', fontSize: 12 }}>
                      {t.originatingBank?.name} · ref {t.reference}
                    </div>
                    {t.notes && (
                      <div style={{ color: '#808080', fontSize: 12 }}>{t.notes}</div>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ color: '#808080', fontSize: 13 }}>No transfers registered yet.</div>
              )}
            </div>

            <div className='nb-payout-drawer__actions'>
              {canRegisterTransfer && (
                <button
                  type='button'
                  className='nb-payout-drawer__btn nb-payout-drawer__btn--primary'
                  onClick={() => setShowTransferModal(true)}
                >
                  Register Transfer
                </button>
              )}
              {payout.status !== 'cancelled' && payout.status !== 'refunded' && (
                <button
                  type='button'
                  className='nb-payout-drawer__btn'
                  onClick={handleHoldToggle}
                >
                  {payout.status === 'on_hold' ? 'Release Hold' : 'Put on Hold'}
                </button>
              )}
              {isAdmin && !['paid', 'cancelled', 'refunded'].includes(payout.status) && (
                <button
                  type='button'
                  className='nb-payout-drawer__btn nb-payout-drawer__btn--danger'
                  onClick={handleCancel}
                >
                  Cancel Payout
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {showTransferModal && payout && (
        <RegisterTransferModal
          isOpen={showTransferModal}
          payoutId={payout.id}
          outstandingBalance={String(payout.outstandingBalance)}
          currency={payout.currency}
          onClose={() => setShowTransferModal(false)}
          onSaved={async () => {
            setShowTransferModal(false)
            await fetchPayout(payout.id)
            onChanged()
          }}
        />
      )}
    </>
  )
}

export default PayoutDetailDrawer
